import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { useEffect, useState } from "react";
import { Toaster } from "sonner";
import "@/App.css";

import Landing from "@/pages/Landing";
import Pricing from "@/pages/Pricing";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import AuthCallback from "@/pages/AuthCallback";
import TenantAdmin from "@/pages/TenantAdmin";
import Billing from "@/pages/Billing";
import SuperAdmin from "@/pages/SuperAdmin";
import Kiosk from "@/pages/Kiosk";

// When launched as an installed PWA (standalone mode), boot straight into the
// last-viewed church kiosk instead of the marketing/login screens.
function isStandalonePWA() {
  try {
    return (
      window.matchMedia?.("(display-mode: standalone)")?.matches ||
      window.navigator?.standalone === true
    );
  } catch {
    return false;
  }
}

function PWALaunchRedirect({ children }) {
  if (isStandalonePWA()) {
    // Admins tapping Staff PIN set this flag to intentionally reach login/admin.
    let override = null;
    try { override = sessionStorage.getItem("admin_override"); } catch { /* ignore */ }
    if (override) {
      try { sessionStorage.removeItem("admin_override"); } catch { /* ignore */ }
      return children;
    }
    let saved = null;
    try { saved = localStorage.getItem("kiosk_slug"); } catch { /* ignore */ }
    if (saved) return <Navigate to={`/kiosk/${saved}`} replace />;
  }
  return children;
}

function ProtectedRoute({ children, role }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-[#2A2626] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (role === "super_admin" && user.role !== "super_admin") return <Navigate to="/admin" replace />;
  return children;
}

// Blocks unprovisioned (unpaid) churches from the app until Stripe confirms an
// active/trialing subscription. Grandfathered + super_admin always pass.
function RequireSubscription({ children }) {
  const { user } = useAuth();
  const [status, setStatus] = useState("checking"); // checking | ok | blocked
  useEffect(() => {
    let alive = true;
    if (user?.role === "super_admin") { setStatus("ok"); return; }
    api.get("/tenant/me")
      .then(({ data }) => { if (alive) setStatus(data.access_granted ? "ok" : "blocked"); })
      .catch(() => { if (alive) setStatus("ok"); });
    return () => { alive = false; };
  }, [user]);
  if (status === "checking") {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-[#2A2626] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (status === "blocked") return <Navigate to="/billing" replace />;
  return children;
}

function AppRoutes() {
  const location = useLocation();
  // Synchronous detection of Google OAuth callback in URL fragment (handle at any route)
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<PWALaunchRedirect><Landing /></PWALaunchRedirect>} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/login" element={<PWALaunchRedirect><Login /></PWALaunchRedirect>} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/admin" element={<ProtectedRoute><RequireSubscription><TenantAdmin /></RequireSubscription></ProtectedRoute>} />
      <Route path="/billing" element={<ProtectedRoute><Billing /></ProtectedRoute>} />
      <Route path="/admin/super" element={<ProtectedRoute role="super_admin"><SuperAdmin /></ProtectedRoute>} />
      <Route path="/kiosk/:slug" element={<Kiosk />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <div className="App">
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </div>
      </BrowserRouter>
    </AuthProvider>
  );
}
